import React, { useCallback, useEffect, useState } from 'react';
import { Search, Download, BadgeDollarSign, CheckCircle2, X } from 'lucide-react';
import api from '../../lib/api';
import { toast } from '../../hooks/use-toast';
import { formatCurrency } from '../../mock';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';

const STATUS = [['', 'Todos'], ['Pending', 'Pending'], ['Funded', 'Funded']];
const EMPTY = { q: '', status: '', lender: '', fi_manager: '', salesperson: '', deal_status: '', date_from: '', date_to: '' };

// Finance → Fundings: acompanhamento financeiro dos deals enviados ao banco.
export const Fundings = () => {
  const [f, setF] = useState(EMPTY);
  const [data, setData] = useState({ rows: [], metrics: {}, filters: {}, can_confirm: false });
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [confirmRow, setConfirmRow] = useState(null);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams(Object.entries(f).filter(([, v]) => v));
      const { data: d } = await api.get(`/finance/fundings?${params.toString()}`);
      setData(d);
    } catch (e) { if (e.response?.status === 403) setDenied(true); }
    finally { setLoading(false); }
  }, [f]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const openConfirm = (r) => { setConfirmRow(r); setAmount(String(r.expected_amount || '')); };

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post(`/finance/fundings/${confirmRow.funding_id}/confirm`, { amount: Number(amount || 0) });
      toast({ title: 'Funding confirmado', description: `${confirmRow.customer_name} · ${formatCurrency(Number(amount || 0))} recebido` });
      setConfirmRow(null);
      await load();
    } catch (e) { toast({ title: 'Erro', description: e.response?.data?.detail || '' }); }
    finally { setBusy(false); }
  };

  const exportCsv = () => {
    const head = ['Cliente', 'Veiculo', 'Stock', 'VIN', 'Deal', 'Banco', 'Vendedor', 'F&I', 'Valor esperado', 'Valor recebido', 'Data venda', 'Enviado em', 'Deal status', 'Funding status', 'Recebido em', 'Confirmado por'];
    const rows = data.rows.map((r) => [r.customer_name, r.vehicle_desc, r.stock, r.vin, r.deal_id, r.lender,
      r.salesperson_name, r.fi_manager, r.expected_amount, r.funding_amount, r.sale_date, (r.submitted_at || '').slice(0, 10),
      r.deal_status, r.funding_status, (r.funded_at || '').slice(0, 10), r.funded_by]);
    const csv = [head, ...rows].map((l) => l.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' }));
    a.download = `fundings-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  if (denied) return <div className="ent-panel p-6 text-sm text-muted-foreground" data-testid="fundings-denied">Somente usuários com acesso financeiro veem os Fundings.</div>;

  const m = data.metrics || {};
  const fl = data.filters || {};
  const active = Object.values(f).some((v) => v);

  return (
    <div className="space-y-3" data-testid="fundings-page">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-border border border-border rounded-sm overflow-hidden" data-testid="fundings-metrics">
        <div className="bg-card px-3 py-2"><p className="ent-label">Pending fundings</p><p className="ent-value mt-1 t-warn" data-testid="fnd-pending">{m.pending ?? 0}</p></div>
        <div className="bg-card px-3 py-2"><p className="ent-label">Pending amount</p><p className="ent-value mt-1 t-warn" data-testid="fnd-pending-amount">{formatCurrency(m.pending_amount || 0)}</p></div>
        <div className="bg-card px-3 py-2"><p className="ent-label">Funded este mês</p><p className="ent-value mt-1 t-ok">{m.funded_month ?? 0}</p></div>
        <div className="bg-card px-3 py-2"><p className="ent-label">Valor recebido no mês</p><p className="ent-value mt-1 t-ok" data-testid="fnd-funded-amount">{formatCurrency(m.funded_amount_month || 0)}</p></div>
      </div>

      <div className="flex flex-wrap items-end gap-2 border-y border-border py-2.5">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground/60" />
          <input data-testid="fnd-search" className="ent-input pl-8" style={{ width: 240 }} value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} placeholder="Cliente, stock, VIN, deal..." />
        </div>
        <div className="flex items-center gap-1.5">
          {STATUS.map(([k, l]) => (
            <button key={k || 'all'} data-testid={`fnd-status-${k || 'all'}`} onClick={() => setF({ ...f, status: k })}
              className={`ent-btn ent-btn-sm ${f.status === k ? 'ent-btn-primary' : ''}`}>{l}</button>
          ))}
        </div>
        <select data-testid="fnd-lender" className="ent-input" style={{ width: 160 }} value={f.lender} onChange={(e) => setF({ ...f, lender: e.target.value })}>
          <option value="">Todos os bancos</option>
          {(fl.lenders || []).map((x) => <option key={x} value={x} className="bg-popover">{x}</option>)}
        </select>
        <select data-testid="fnd-fi" className="ent-input" style={{ width: 160 }} value={f.fi_manager} onChange={(e) => setF({ ...f, fi_manager: e.target.value })}>
          <option value="">Todos F&I</option>
          {(fl.fi_managers || []).map((x) => <option key={x} value={x} className="bg-popover">{x}</option>)}
        </select>
        <select data-testid="fnd-salesperson" className="ent-input" style={{ width: 160 }} value={f.salesperson} onChange={(e) => setF({ ...f, salesperson: e.target.value })}>
          <option value="">Todos vendedores</option>
          {(fl.salespeople || []).map((x) => <option key={x} value={x} className="bg-popover">{x}</option>)}
        </select>
        <select data-testid="fnd-deal-status" className="ent-input" style={{ width: 180 }} value={f.deal_status} onChange={(e) => setF({ ...f, deal_status: e.target.value })}>
          <option value="">Qualquer stage do deal</option>
          {(fl.deal_statuses || []).map((x) => <option key={x} value={x} className="bg-popover">{x}</option>)}
        </select>
        <input data-testid="fnd-from" type="date" className="ent-input" style={{ width: 140 }} value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} />
        <input data-testid="fnd-to" type="date" className="ent-input" style={{ width: 140 }} value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} />
        {active && <button data-testid="fnd-clear" onClick={() => setF(EMPTY)} className="ent-btn ent-btn-sm"><X className="w-3 h-3" /> Limpar</button>}
        <button data-testid="fnd-export" onClick={exportCsv} disabled={!data.rows.length} className="ent-btn ent-btn-sm ml-auto disabled:opacity-40"><Download className="w-3 h-3" /> CSV</button>
      </div>

      <div className="overflow-x-auto">
        <table className="ent-table" data-testid="fundings-table">
          <thead><tr>
            <th>Cliente</th><th>Veículo</th><th>Deal #</th><th>Banco</th>
            <th className="text-right">Funding</th><th>Venda</th><th>Deal status</th><th>Funding status</th><th />
          </tr></thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.funding_id} data-testid={`fnd-row-${r.deal_id}`}>
                <td className="font-medium max-w-[170px] truncate">{r.customer_name}
                  <span className="block text-[10px] t-label">{r.salesperson_name} · F&I {r.fi_manager || '—'}</span>
                </td>
                <td className="max-w-[210px]"><span className="truncate block">{r.vehicle_desc}</span>
                  <span className="mono text-[10px] t-label">#{r.stock}{r.vin ? ` · ${r.vin.slice(-6)}` : ''}</span>
                </td>
                <td className="num text-[11px]">{r.deal_id}</td>
                <td className="text-[11.5px] max-w-[120px] truncate">{r.lender || r.finance_type || '—'}</td>
                <td className="num text-right">
                  {r.funding_status === 'Funded' ? <span className="t-ok">{formatCurrency(r.funding_amount)}</span> : formatCurrency(r.expected_amount)}
                  {r.funding_status === 'Funded' && Math.abs((r.funding_amount || 0) - (r.expected_amount || 0)) > 0.5 && (
                    <span className="block text-[10px] t-warn">esperado {formatCurrency(r.expected_amount)}</span>
                  )}
                </td>
                <td className="num text-[11px]">{r.sale_date || '—'}</td>
                <td><span className="ent-tag">{r.deal_status}</span>{r.canceled && <span className="ent-tag t-bad ml-1">CANCELADA</span>}</td>
                <td>
                  <span className={`ent-status ${r.funding_status === 'Funded' ? 't-ok' : 't-warn'}`}><span className="ent-dot" />{r.funding_status}</span>
                  {r.funding_status === 'Funded' && <span className="block text-[10px] t-label">{(r.funded_at || '').slice(0, 10)} · {r.funded_by}</span>}
                </td>
                <td className="text-right whitespace-nowrap">
                  {r.funding_status === 'Pending' && data.can_confirm && (
                    <button data-testid={`fnd-confirm-${r.deal_id}`} onClick={() => openConfirm(r)} className="ent-btn ent-btn-sm"><CheckCircle2 className="w-3 h-3" /> Confirm Funding</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && !data.rows.length && <p className="ent-empty" data-testid="fundings-empty">Nenhum funding com esses filtros. Os registros são criados quando o F&I clica em "Send to Funding & Continue to Service" no deal.</p>}
        {loading && <p className="ent-empty">Carregando fundings...</p>}
      </div>

      <Dialog open={!!confirmRow} onOpenChange={(o) => !o && setConfirmRow(null)}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border text-foreground max-w-md" data-testid="fnd-confirm-dialog">
          <DialogHeader><DialogTitle className="font-display font-bold text-lg uppercase tracking-tight flex items-center gap-2"><BadgeDollarSign className="w-4 h-4 text-primary" /> Confirm Funding Received</DialogTitle></DialogHeader>
          <div className="space-y-2.5 text-[13px]">
            <div className="grid grid-cols-2 gap-y-1.5">
              <span className="t-label">Cliente</span><span className="font-medium text-right">{confirmRow?.customer_name}</span>
              <span className="t-label">Veículo</span><span className="text-right">{confirmRow?.vehicle_desc}</span>
              <span className="t-label">Stock</span><span className="text-right mono">#{confirmRow?.stock}</span>
              <span className="t-label">Banco / Lender</span><span className="text-right">{confirmRow?.lender || confirmRow?.finance_type || '—'}</span>
              <span className="t-label">Expected funding</span><span className="text-right num font-semibold">{formatCurrency(confirmRow?.expected_amount || 0)}</span>
              <span className="t-label">Deal status</span><span className="text-right">{confirmRow?.deal_status}</span>
            </div>
            <div>
              <p className="ent-label mb-1">Actual funding amount recebido</p>
              <input data-testid="fnd-confirm-amount" type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                className="w-full bg-muted/40 border border-border rounded-md px-3 py-2 text-[14px] num outline-none focus:border-primary transition-colors" />
            </div>
            <p className="text-[11.5px] t-label">Esta ação apenas confirma o dinheiro recebido do banco — o stage operacional do deal não muda.</p>
          </div>
          <DialogFooter>
            <button data-testid="fnd-confirm-cancel" onClick={() => setConfirmRow(null)} className="ent-btn">Voltar</button>
            <button data-testid="fnd-confirm-save" onClick={confirm} disabled={busy} className="ent-btn ent-btn-primary">{busy ? 'Confirmando...' : 'Confirmar recebimento'}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Fundings;
